import type { UserMeResponse } from '@/api/schema'
import type {
  CalendarDaysResponse,
  CalendarMoonDayResponse,
  CalendarMoonFullResponse,
  CalendarMoonNewResponse,
  CalendarMoonPhaseResponse,
  CalendarMoonSignResponse,
  CalendarTimelineDay,
} from '@/api/schema/calendar'
import type { MatrixArcanaResponse } from '@/api/schema/matrix-arcana'
import { createContext } from 'preact'
import { useContext, useMemo, useState } from 'preact/hooks'

export type AppSessionData = UserMeResponse | null

export type CalendarSSRData =
  | {
    type: 'calendar'
    response: CalendarDaysResponse
    timeline: CalendarTimelineDay[]
  }
  | {
    type: 'moon-day'
    response: CalendarMoonDayResponse
  }
  | {
    type: 'moon-sign'
    response: CalendarMoonSignResponse
  }
  | {
    type: 'moon-full'
    response: CalendarMoonFullResponse
  }
  | {
    type: 'moon-new'
    response: CalendarMoonNewResponse
  }
  | {
    type: 'moon-phase'
    response: CalendarMoonPhaseResponse
  }

export type CalendarSSRPayload = {
  routeKey: string
  data: CalendarSSRData
}

export type AppData = {
  session?: AppSessionData
  matrixArcana?: MatrixArcanaResponse | null
  matrixMoneyArcana?: MatrixArcanaResponse | null
  calendar?: CalendarSSRPayload
}

export const EMPTY_APP_DATA: AppData = {}

export const AppDataContext = createContext<AppData>(EMPTY_APP_DATA)

export function useAppData() {
  const ctx = useContext(AppDataContext)

  return useMemo(() => ({
    session: ctx.session,
    getMatrixArcana(id: number) {
      return ctx.matrixArcana?.arcana.id === id
        ? ctx.matrixArcana
        : null
    },
    getMoneyArcana(id: number) {
      return ctx.matrixMoneyArcana?.arcana.id === id
        ? ctx.matrixMoneyArcana
        : null
    },
  }), [ctx])
}

export function useCalendarSSRData(routeKey: string): CalendarSSRData | null {
  const appData = useContext(AppDataContext)

  return useState(() => {
    const payload = appData.calendar

    if (!payload || payload.routeKey !== routeKey) {
      return null
    }

    if (typeof window !== 'undefined') {
      delete appData.calendar
    }

    return payload.data
  })[0]
}

export function getClientAppData(): AppData {
  if (typeof window === 'undefined') {
    return EMPTY_APP_DATA
  }

  return window.__APP_DATA__ ?? EMPTY_APP_DATA
}

declare global {
  interface Window {
    __APP_DATA__?: AppData
  }
}
